import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./App.css";
import ProjectModal from "./ProjectModal";
import { Project, ProjectTag, TagConfig } from "./types";

const tagConfigs: Record<ProjectTag, TagConfig> = {
  [ProjectTag.PROFESSIONAL_SOFTWARE]: {
    color: "#3c402c",    // Olive green
    darkColor: "#2e440eff", // Lime green
  },
  [ProjectTag.CONTRACT_WORK]: {
    color: "#3c402c",    // Olive green
    darkColor: "#2e440eff", // Lime green
  },
  [ProjectTag.PERSONAL_PROJECT]: {
    color: "#0369a1",    // Retro blue
    darkColor: "#032c3fff", // Cyan blue
  },
  [ProjectTag.IN_DEVELOPMENT]: {
    color: "#dc2626",    // Retro red
    darkColor: "#860000ff", // Bright red
  },
  [ProjectTag.OPEN_SOURCE]: {
    color: "#d97706",    // Burnt orange
    darkColor: "#662f03ff", // Bright orange
  },
};

function App() {
  
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem("darkMode");
    return saved ? JSON.parse(saved) : true;
  });
  const [currentSection, setCurrentSection] = useState(0);
  const [showScrollIndicator, setShowScrollIndicator] = useState(true);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
    localStorage.setItem("darkMode", JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  useEffect(() => {
    const sections = ["about", "skills", "projects"];
    const observerOptions = {
      root: null,
      rootMargin: "0px",
      threshold: 0.5,
    };

    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionIndex = sections.indexOf(entry.target.id);
          setCurrentSection(sectionIndex);
          setShowScrollIndicator(sectionIndex < sections.length - 1);
        }
      });
    };

    const observer = new IntersectionObserver(
      handleIntersection,
      observerOptions,
    );

    sections.forEach((sectionId) => {
      const element = document.getElementById(sectionId);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [isDarkMode]);

  // Wrap skill list items per-character for wave animation on hover
  useEffect(() => {
    const wrapSkills = () => {
      const els = Array.from(document.querySelectorAll('.skill-category li')) as HTMLLIElement[];
      const palette = ['#FF3B30', '#FF9500', '#FFD60A', '#32D74B', '#5AC8FA', '#5856D6', '#FF2D55'];

      els.forEach((el) => {
        const text = el.textContent ?? '';
        if (!text.trim()) return;

        // clear existing children and build spans per character
        el.innerHTML = '';
        for (let i = 0; i < text.length; i++) {
          const ch = text[i];
          const span = document.createElement('span');
          span.className = 'skill-char';
          span.style.setProperty('--i', String(i));
          span.textContent = ch;
          // store palette index for hover coloring
          (span as any).dataset.color = palette[i % palette.length];
          el.appendChild(span);
        }
      });
    };

    // Run immediately and after delays to catch all renders
    wrapSkills();
    const timer1 = setTimeout(wrapSkills, 50);
    const timer2 = setTimeout(wrapSkills, 200);
    const timer3 = setTimeout(wrapSkills, 500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  // Procedural retro starfield background (canvas)
  useEffect(() => {
    const c = document.getElementById('starfield');
    if (!(c instanceof HTMLCanvasElement)) return;
    const canvasEl: HTMLCanvasElement = c;
    const ctx = canvasEl.getContext('2d');
    if (!ctx) return;
    const ctx2 = ctx;

    // toggle light/dark background variant
    const bg = document.getElementById('starfield-bg');
    try {
      if (bg) {
        if (!isDarkMode) bg.classList.add('light');
        else bg.classList.remove('light');
      }
    } catch (e) {}

    let width = window.innerWidth;
    let height = window.innerHeight;
    const dpr = Math.max(1, window.devicePixelRatio || 1);

    interface Star { angle: number; radius: number; size: number; angularSpeed: number; radialSpeed: number; alpha: number; offset: number; twinkleSpeed: number; colorVariant?: 'white' | 'green' | 'black'; }
    let stars: Star[] = [];

    function resize() {
      width = window.innerWidth;
      height = window.innerHeight;
      canvasEl.style.width = width + 'px';
      canvasEl.style.height = height + 'px';
      canvasEl.width = Math.floor(width * dpr);
      canvasEl.height = Math.floor(height * dpr);
      ctx2.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function initStars() {
      stars = [];
      const maxRadius = Math.max(width, height) * 0.9;
      // tune count and colors based on theme
      const count = isDarkMode ? Math.max(1000, Math.floor((width * height) / 2000)) : Math.max(1000, Math.floor((width * height) / 2000));
      for (let i = 0; i < count; i++) {
        const s: Star = {
          angle: Math.random() * Math.PI * 2,
          radius: Math.random() * maxRadius,
          size: 0.6 + Math.random() * 2,
          angularSpeed: (0.0006 + Math.random() * 0.003) * (Math.random() > 0.5 ? 1 : -1),
          radialSpeed: 0.01 + Math.random() * 0.03,
          alpha: isDarkMode ? 0.6 + Math.random() * 0.5 : 0.6 + Math.random() * 0.45,
          offset: Math.random() * 10,
          twinkleSpeed: 0.6 + Math.random() * 1.6,
                colorVariant: isDarkMode ? 'white' : 'black',
        };
        stars.push(s);
      }
    }

    let last = performance.now();
    let raf = 0;

    function draw(now: number) {
      const dt = Math.min(50, now - last) / 16.6667;
      last = now;

      // keep canvas fully transparent — clear each frame so no breathing overlay
      ctx2.clearRect(0, 0, width, height);

      const cx = width / 2;
      const cy = height / 2;

      for (let i = 0; i < stars.length; i++) {
        const s = stars[i];
        s.angle += s.angularSpeed * dt;
        s.radius += s.radialSpeed * dt;
        const x = cx + Math.cos(s.angle) * s.radius;
        const y = cy + Math.sin(s.angle) * s.radius;
        // make diamond points crisp: use integer coords and disable smoothing temporarily
        const t = now * 0.001;
        const tw = 0.5 + 0.5 * Math.sin((t + s.offset) * s.twinkleSpeed);
        const alpha = Math.max(0.05, Math.min(1, s.alpha * tw));
        const size = s.size;
        const ix = Math.round(x);
        const iy = Math.round(y);
        const isz = Math.max(1, Math.round(size));
        try { ctx2.imageSmoothingEnabled = false; } catch (e) {}
        const variant = s.colorVariant ?? (isDarkMode ? 'white' : 'black');
        let sr = 255, sg = 255, sb = 255;
        if (variant === 'green') { sr = 160; sg = 255; sb = 180; }
        else if (variant === 'black') { sr = 24; sg = 24; sb = 24; }
        ctx2.fillStyle = `rgba(${sr},${sg},${sb},${alpha})`;
        ctx2.beginPath();
        ctx2.moveTo(ix, iy - Math.round(isz * 1.4));
        ctx2.lineTo(ix + isz, iy);
        ctx2.lineTo(ix, iy + Math.round(isz * 1.4));
        ctx2.lineTo(ix - isz, iy);
        ctx2.closePath();
        ctx2.fill();
        try { ctx2.imageSmoothingEnabled = false; } catch (e) {}
      }

      raf = requestAnimationFrame(draw);
    }

    resize();
    initStars();
    raf = requestAnimationFrame(draw);

   

    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, [isDarkMode]);

  const toggleDarkMode = useCallback(() => {
    setIsDarkMode(!isDarkMode);
  }, [isDarkMode]);

  // Ensure `#starfield-bg` class updates immediately when theme toggles
  useEffect(() => {
    const bg = document.getElementById('starfield-bg');
    if (!bg) return;
    if (isDarkMode) bg.classList.remove('light');
    else bg.classList.add('light');
  }, [isDarkMode]);

  const getTagStyle = useCallback(
    (tag: ProjectTag) => {
      const config = tagConfigs[tag];
      const color = isDarkMode ? config.darkColor : config.color;
      return { backgroundColor: color, color: "white" };
    },
    [isDarkMode],
  );

  const openModal = useCallback((project: Project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProject(null);
  }, []);

  const scrollToNextSection = useCallback(() => {
    const sections = ["about", "skills", "projects"];
    const nextIndex = Math.min(currentSection + 1, sections.length - 1);
    const nextSection = document.getElementById(sections[nextIndex]);
    if (nextSection) {
      nextSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [currentSection]);

  const getSectionTitle = useCallback(() => {
    const titles = ["Skills", "Projects", ""];
    return titles[currentSection] || "";
  }, [currentSection]);

  const projects: Project[] = useMemo(
    () => [
      {
        id: "1",
        title: "Arborist Report Generator",
        shortDescription:
          "Automated report generation tool for professional arborists",
        longDescription:
          "<p>Automated report generation tool that reduced arborist report creation time from hours to minutes through smart data integration and template automation.</p><h3>Key Features</h3><ul><li>Automated data flow from field assessments to final reports</li><li>GIS integration for mapping and location data</li><li>Multiple export formats (PDF, Word, Excel)</li><li>Council compliance checking</li><li>Photo management with automatic insertion</li></ul><h3>Impact</h3><ul><li>80% reduction in report generation time</li><li>Eliminated duplicate data entry</li><li>Increased business capacity without additional staff</li></ul>",
        technologies: [
          "C++",
          "Qt",
          "SQL",
          "PDF Generation",
          "GIS Integration",
          "MS Office Integration",
        ],
        images: [
          {
            path: "/images/report_image.PNG",
            caption:
              "Report creation screen with data entry fields.",
          },
				],
        tags: [ProjectTag.CONTRACT_WORK, ProjectTag.PROFESSIONAL_SOFTWARE],
      },
      {
        id: "2",
        title: "S3DX Viewer",
        shortDescription:
          "Web-based 3D viewer for surfboard design files with advanced mesh generation",
        longDescription:
          "<p>Professional web-based 3D viewer for S3DX surfboard design files with advanced mesh generation algorithms.</p><h3>Key Features</h3><ul><li>S-Linear interpolation mesh generation with Bezier curve evaluation</li><li>Real-time WebGL rendering and interactive 3D controls</li><li>Drag-and-drop file support with XML parsing and validation</li><li>Cross-section interpolation and natural nose/tail closure</li><li>Responsive design with real-time board specifications</li></ul></p>",
        technologies: [
          "React",
          "TypeScript",
          "Three.js",
          "WebGL",
          "Maths",
          "XML Parsing",
          "Vite",
        ],
        tags: [ProjectTag.PERSONAL_PROJECT, ProjectTag.OPEN_SOURCE],
        liveUrl: "https://s3dx.jfpardy.com",
        githubUrl: "https://github.com/jfpardy/s3dx-viewer",
      },
      {
        id: "3",
        title: "Trident",
        shortDescription:
          "Cross-platform surfboard CAD viewer with real-time 3D visualization",
        longDescription:
          "<p>High-performance CAD viewer for surfboard designers, featuring real-time 3D visualization and accurate curve rendering for Shape3D and AKU Shaper files.</p><h3>Technical Highlights</h3><ul><li>Custom XML parser with multi-segment Bezier curve implementation</li><li>Hardware-accelerated OpenGL 3.3 renderer with 4x MSAA</li><li>From-scratch immediate-mode GUI using STB libraries</li><li>DES decryption for encrypted AKU Shaper files</li></ul><h3>Key Features</h3><ul><li>Multi-viewport synchronized views (3D, outline, cross-sections)</li><li>Real-time curvature analysis visualization</li><li>Interactive control point editing</li><li>60+ FPS with complex designs</li><li>Cross-platform (Linux, macOS, Windows)</li></ul>",
        technologies: [
          "Odin",
          "OpenGL",
          "3D Graphics and Mesh Generation",
          "SDL3",
          "Custom IMGUI",
          "STB Libraries",
          "XML Parsing",
          "Maths",
        ],
        images: [
          {
            path: "/images/trident-image-1.png",
            caption:
              "Outline editor with curvature visualisation shown.",
          },
          {
            path: "/images/trident-image-2.png",
            caption:
              "3D render view",
          },
          {
            path: "/images/trident-photo.png",
            caption:
              "Machine cut blank.",
          },
        ],
        tags: [ProjectTag.PERSONAL_PROJECT, ProjectTag.IN_DEVELOPMENT],
      },
    ],
    [],
  );

  return (
    <>
      <div id="starfield-bg" aria-hidden="true" />
      <canvas id="starfield" aria-hidden="true" />
      <div id="crt-container">
        <div className="crt-inner">
          <div className="App">
            <nav className="nav">
        <div className="nav-links">
          <a href="#about" className={currentSection === 0 ? "active" : ""}>About</a>
          <a href="#skills" className={currentSection === 1 ? "active" : ""}>Skills</a>
          <a href="#projects" className={currentSection === 2 ? "active" : ""}>Projects</a>
          <a href="http://s3dx.jfpardy.com">Shape3d Viewer</a>
          <a href="https://battletris.jfpardy.com">Battletris</a>
        </div>
        <button
          className="theme-toggle"
          onClick={toggleDarkMode}
          aria-label={
            isDarkMode ? "Switch to light mode" : "Switch to dark mode"
          }
        >
          <span className="toggle-track">
            <span className={`toggle-thumb ${isDarkMode ? "dark" : ""}`} />
          </span>
          <span className="toggle-icon">{isDarkMode ? "" : ""}</span>
          <span className="toggle-text">{isDarkMode ? "Dark Mode" : "Light Mode"}</span>
        </button>
      </nav>

      <main>
        <section id="about" className="section">
          <div className="profile-content">
            <h1>Joseph Pardy</h1>
            <p>Software developer and IT Specialist.</p>
            <br></br>
            <p>
              Focused on systems programming, and automation. 
              Passionate about low-level programming and systems engineering.
              <br></br>Based in Melbourne
            </p>
          </div>
          <div className="contact-links">
            <a
              href="https://github.com/jfpardy"
              target="_blank"
              rel="noopener noreferrer"
            >
              GitHub <img src="/images/GitHub-Icon.png" width="16" className="github-icon" alt=""></img>
            </a>
            <a href="mailto:josephfpardy@gmail.com">Email <img src="/images/Mail-Icon.png" width="16" alt=""></img></a>
          </div>
        </section>

        <section id="skills" className="section">
          <h2>Experience</h2>
          <div className="skills-grid">
            <div className="skill-category">
              <h3>Programming Languages</h3>
              <ul>
                <li>C++</li>
                <li>C</li>
                <li>Python</li>
                <li>Odin</li>
                <li>Javascript</li>
                <li>Visual Basic</li>
              </ul>
            </div>
            <div className="skill-category">
              <h3>Tools</h3>
              <ul>
                <li>Qt</li>
                <li>MongoDB</li>
                <li>Django</li>
                <li>Win32 programming</li>
                <li>Linux</li>
              </ul>
            </div>
          </div>
        </section>

        <section id="projects" className="section">
          <h2>Projects</h2>
          <div className="projects-grid">
            {projects.map((project) => (
              <div
                key={project.id}
                className="project-card clickable"
                onClick={() => openModal(project)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openModal(project);
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for ${project.title}`}
              >
                <h3>{project.title}</h3>
                <p>{project.shortDescription}</p>
                <div className="project-meta">
                  {project.tags && (
                    <div className="project-tags-inline">
                      {project.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="project-tag-small"
                          style={getTagStyle(tag)}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="project-technologies">
                    {project.technologies.map((tech, index) => (
                      <span key={index} className="tech-tag-small">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>

      <ProjectModal
        project={selectedProject}
        isOpen={isModalOpen}
        onClose={closeModal}
        getTagStyle={getTagStyle}
      />

      {showScrollIndicator && (
        <div
          className={`scroll-indicator ${!showScrollIndicator ? "hide" : ""}`}
          onClick={scrollToNextSection}
          role="button"
          tabIndex={0}
          aria-label={`Scroll to ${getSectionTitle()}`}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              scrollToNextSection();
            }
          }}
        >
          <div className="scroll-arrow" />
        </div>
      )}
          </div>
        </div>
      </div>
    </>
  );
}

export default App;

